<template>
  <div class="ChannelConfiguration paddedPage">
    <div class="flex">
      <div class="menu">
        <div class="inputGroup">
          <label>
            I/O mode
          </label>
          <Dropdown optionLabel="label" optionValue="value"
            :modelValue="ioMode"
            :options="ioModeOptions"
            @update:modelValue="setIoMode"
          ></Dropdown>
          <Button v-if="showingLoadInputValuesButton" severity="secondary" @click="confirmLoadInputValues">
            <i class="pi pi-file-arrow-up"></i>
            Load previous input values
            <ConfirmPopup>
              <template #container>
                <div class="p-confirm-popup p-component">
                  <div class="p-confirm-popup-content" style="width: 270px;">
                    <template v-if="hasManualInputsFile">
                      Really load previous input values {{ ioMode == 3 ? 'for selected inputs' : null }}?
                    </template>
                    <template v-else>
                      No previous input values to load.
                    </template>
                  </div>
                  <div class="p-confirm-popup-footer">
                    <Button v-if="hasManualInputsFile" size="small" severity="secondary" @click="onClickConfirmLoadPreviousCancel()">
                      Cancel
                    </Button>
                    <Button size="small" @click="onClickConfirmLoadPreviousOk()">
                      Ok
                    </Button>
                  </div>
                </div>
              </template>
            </ConfirmPopup>
          </Button>
        </div>
        <div class="inputGroup">
          <label>
            Sensor model configuration
          </label>
          <Dropdown optionLabel="label" optionValue="value"
            :modelValue="sensorModelConfigurationMode"
            :options="sensorModelConfigurationOptions"
            @update:modelValue="setSensorModelConfigurationMode"
          ></Dropdown>
        </div>
        <div class="inputGroup">
          <label>
            Port
          </label>
          <Dropdown optionLabel="label" optionValue="value"
            v-model="selectedPortNumber"
            :options="portOptions"
          ></Dropdown>
        </div>
        <div class="inputGroup">
          <label>
            Channels
          </label>
          <Button v-for="channelGroup in channelGroups" class="channelGroup" severity="secondary"
            :class="channelGroupButtonClass(channelGroup)"
            @click="selectChannelGroup(channelGroup)"
          >
            {{ channelGroup.label }}
          </Button>
        </div>
      </div>
      <component :is="channelGroupComponent" class="viewComponent"
        :ioMode="ioMode"
        :port="selectedPort"
        :portNumber="selectedPortNumber"
        :channels="selectedChannelGroup.channels"
        :channelOffset="selectedChannelGroup.channelOffset"
        :channelMap="channelMap"
        :sensorModelConfigurationMode="sensorModelConfigurationMode"
      ></component>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import { useConfirm } from "primevue/useconfirm";
import ConfigureControllerGpio from "./ChannelConfigurationGpio.vue";
import ConfigureControllerAniGroup from "./ChannelConfigurationAniGroup.vue";
import ConfigureControllerAnoGroup from "./ChannelConfigurationAnoGroup.vue";
import store from "./store.js";
import { createChannelMap } from "./appUtility.js";

let ioMode = computed(() => {
  let session = store.sessions[0];
  let system = session.system;
  return system.controller.ioMode.value;
});

let ioModeOptions = [
  { label: "Automatic", value: 0 },
  { label: "Manual all", value: 1 },
  { label: "Manual inputs", value: 2 },
  { label: "Manual selected", value: 3 },
  { label: "Unused outputs", value: 4 }
];

// let showingLoadInputValuesButton = computed(() => true);
let showingLoadInputValuesButton = computed(() => (
  ioMode.value == 1 ||
  ioMode.value == 2 ||
  ioMode.value == 3
));

function setIoMode(value) {
  let session = store.sessions[0];
  session.queueRequest("/cmd/setIoMode", { mode: value });
}

let hasManualInputsFile = computed(() => {
  let session = store.sessions[0];
  return session.system.controller.hasManualInputsFile.value;
});

let confirm = useConfirm();
function confirmLoadInputValues(event) {
  confirm.require({ target: event.currentTarget });
}

function onClickConfirmLoadPreviousCancel() {
  confirm.close();
}

function onClickConfirmLoadPreviousOk() {
  let session = store.sessions[0];
  session.queueRequest("/cmd/loadManualInputValues");
  confirm.close();
}

let sensorModelConfigurationMode = computed(() => {
  let session = store.sessions[0];
  let system = session.system;
  let isManual = (system.controller.autoConfigureMask.value == "00:00:00:00:00:00");
  return isManual ? "manual" : "auto";
});

let sensorModelConfigurationOptions = [
  { label: "Manual", value: "manual" },
  { label: "Automatic", value: "auto" }
];

function setSensorModelConfigurationMode(mode) {
  let session = store.sessions[0];
  let system = session.system;

  let mask = null;
  if (mode == "manual") mask = "00:00:00:00:00:00";
  if (mode == "auto") mask = system.macAddr.value;
  session.changeSettings([{ 
    handle: system.controller.autoConfigureMask.handle, 
    value: mask 
  }]);
}

let selectedPortNumber = ref(0);

let portOptions = computed(() => {
  let session = store.sessions[0];
  let system = session.system;
  let ports = system.controller.ports.children;
  let options = ports.map((port, portNumber) => ({
    label: portLabel(portNumber),
    value: portNumber
  }));
  return options;
});

let selectedPort = computed(() => {
  let session = store.sessions[0];
  let system = session.system;
  let ports = system.controller.ports.children;
  return ports[selectedPortNumber.value];
});

let channelMap = computed(() => {
  return createChannelMap(store.sessions, store.sessions[0].system);
});

function portLabel(portNumber) {
  let session = store.sessions[0];
  let system = session.system;
  let port = system.controller.ports.children[portNumber];
  let portName = `Port ${(['E','F','C','D'])[portNumber]}`;
  let portVersion = `(v${port.versionMajor.value}.${port.versionMinor.value})`;
  if (port.versionNumber.value)
    return `${portName} ${portVersion}`
  else
    return `${portName}`;
}

let selectedChannelGroupId = ref("gpio");

let selectedChannelGroup = computed(() => {
  return channelGroups.value.find(group => group.id == selectedChannelGroupId.value);
});

let channelGroupComponent = computed(() => {
  return selectedChannelGroup.value.component;
});

let channelGroups = computed(() => {
  let saiChannelIndex = selectedPortNumber.value * 24;
  let saoChannelIndex = selectedPortNumber.value * 12;
  return [
    { 
      id: "gpio",
      component: ConfigureControllerGpio,
      label: "SDI, SDO",
      channels: selectedPort.value.digitalInputs.children,
      channelOffset: 0
    },
    {
      id: "saiGroup-1",
      component: ConfigureControllerAniGroup,
      label: `SAI ${saiChannelIndex + 1} - ${saiChannelIndex + 8}`,
      channels: selectedPort.value.analogInputs.children.slice(0, 8),
      channelOffset: 0,
    },
    {
      id: "saiGroup-2",
      component: ConfigureControllerAniGroup,
      label: `SAI ${saiChannelIndex + 9} - ${saiChannelIndex + 16}`,
      channels: selectedPort.value.analogInputs.children.slice(8, 16),
      channelOffset: 8,
    },
    {
      id: "saiGroup-4",
      component: ConfigureControllerAniGroup,
      label: `SAI ${saiChannelIndex + 17} - ${saiChannelIndex + 24}`,
      channels: selectedPort.value.analogInputs.children.slice(16, 24),
      channelOffset: 16
    },
    {
      id: "saoGroup-1",
      component: ConfigureControllerAnoGroup,
      label: `SAO ${saoChannelIndex + 1} - ${saoChannelIndex + 8}`,
      channels: selectedPort.value.analogOutputs.children.slice(0, 8),
      channelOffset: 0
    },
    {
      id: "saoGroup-2",
      component: ConfigureControllerAnoGroup,
      label: `SAO ${saoChannelIndex + 9} - ${saoChannelIndex + 12}`,
      channels: selectedPort.value.analogOutputs.children.slice(8, 16),
      channelOffset: 8
     }
  ];
});

function channelGroupButtonClass(channelGroup) {
  return {
    selected: (channelGroup.id == selectedChannelGroupId.value)
  };
}

function selectChannelGroup(channelGroup) {
  selectedChannelGroupId.value = channelGroup.id;
}
</script>

<style scoped>
.ChannelConfiguration {
  overflow: auto;
}
.flex {
  flex-grow: 1;
  display: flex;
  gap: 30px;
}
.menu {
  width: 270px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.menu .inputGroup {
  display: flex;
  flex-direction: column;
  gap: 5px;
}
.menu button.channelGroup {
  font-family: Reddit Mono;
}
.menu button.channelGroup.selected {
  color: #222;
  background: var(--blue-200);
  border: 1px solid var(--blue-300);
}
:deep(.viewComponent) {
  flex-grow: 1;
}
</style>